/**
 * Microcourse Studio — 统一导出菜单
 * 支持: 独立 HTML / PDF(打印) / JSON 项目文件 / 分享链接
 */

import { useState } from 'react'
import type { MicrocourseProject } from '@ai-tutor/shared'
import { downloadHTML, downloadJSON } from './generateHTML'
import * as lessonApi from '@/api/lesson'

export interface ExportMenuProps {
  lessonId: string
  /** 当前课件项目数据（用于 HTML/JSON 导出） */
  project?: MicrocourseProject | null
  /** 按钮尺寸 */
  size?: 'sm' | 'md'
}

export function ExportMenu({ lessonId, project, size = 'sm' }: ExportMenuProps) {
  const [open, setOpen] = useState(false)
  const [shareLink, setShareLink] = useState<string | null>(null)
  const [copying, setCopying] = useState(false)
  const [generating, setGenerating] = useState(false)

  const handleExportHTML = () => {
    if (!project) return
    downloadHTML(project)
    setOpen(false)
  }

  const handleExportJSON = () => {
    if (!project) return
    downloadJSON(project)
    setOpen(false)
  }

  const handleExportPDF = () => {
    window.open(`#/teacher/${lessonId}/print`, '_blank')
    setOpen(false)
  }

  const handleShare = async () => {
    setGenerating(true)
    try {
      const { token } = await lessonApi.createShareToken(lessonId)
      const link = `${window.location.origin}/#/share/${token}`
      setShareLink(link)
    } catch {
      // ignore
    } finally {
      setGenerating(false)
    }
  }

  const handleCopyLink = async () => {
    if (!shareLink) return
    try {
      await navigator.clipboard.writeText(shareLink)
      setCopying(true)
      setTimeout(() => setCopying(false), 2000)
    } catch {
      const input = document.createElement('input')
      input.value = shareLink
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopying(true)
      setTimeout(() => setCopying(false), 2000)
    }
  }

  const btnClass =
    size === 'sm'
      ? 'px-3 py-1.5 text-xs'
      : 'px-4 py-2 text-sm'

  return (
    <div className="relative">
      <button
        className={`${btnClass} border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium`}
        onClick={() => setOpen(!open)}
      >
        导出 ▾
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          {/* Menu */}
          <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-white dark:bg-gray-900 border rounded-lg shadow-lg p-2 space-y-1">
            {/* Standalone HTML */}
            <button
              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={handleExportHTML}
              disabled={!project}
              title={!project ? '课件尚未就绪' : '导出为可独立运行的 reveal.js HTML 文件'}
            >
              🌐 导出 HTML（独立文件）
            </button>

            {/* PDF / Print */}
            <button
              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2"
              onClick={handleExportPDF}
            >
              🖨️ 导出 PDF（打印）
            </button>

            {/* JSON project */}
            <button
              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={handleExportJSON}
              disabled={!project}
              title={!project ? '课件尚未就绪' : '导出 JSON 项目文件（可导入）'}
            >
              📦 导出 JSON（项目文件）
            </button>

            <div className="border-t my-1" />

            {/* Share link */}
            {!shareLink ? (
              <button
                className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted flex items-center gap-2 disabled:opacity-40"
                onClick={handleShare}
                disabled={generating}
              >
                {generating ? '⏳ 生成中...' : '🔗 生成分享链接'}
              </button>
            ) : (
              <div className="px-3 py-2 space-y-2">
                <div className="text-xs text-muted-foreground">分享链接：</div>
                <div className="flex gap-1">
                  <input
                    readOnly
                    value={shareLink}
                    className="flex-1 text-xs border rounded px-2 py-1 bg-muted"
                  />
                  <button
                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    onClick={handleCopyLink}
                  >
                    {copying ? '✓' : '复制'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
